import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import { createMemoryStore } from "../../../lib/store";
import { displayToast } from "../../../components/app/AppToast";
import type { SuggestedRoom, SuggestedTopic } from "../transcript";
import {
  gaEventGptRoomSuggestionShown,
  gaEventGptRoomSuggestionClicked,
  gaEventGptRoomSuggestionDismissed,
} from "../../../tools/analytics/domains/gpt.analytics";
import { usePracticeArm } from "../../practice/hooks/usePracticeArm";
import type { ConversationEngine } from "../../assistant/conversationEngine";
import { uiMessageSuggestion, type GptChatInstance } from "./useGptChat";

/**
 * The agent's proposal for the LATEST turn, or `null` when there is nothing to
 * propose. Returns DATA + callbacks (not UI): the card is rendered by
 * `GptConversation` in the composer band, in place of the input.
 *
 * ONE proposal, never two — the band holds a single widget, so this is a
 * discriminated union rather than two independent hooks the caller would have to
 * prioritize. The arbitration itself already happened server-side; this hook
 * only filters what became stale between the answer and the render.
 *
 * A proposal is LIVE-ONLY: it exists while the user is in the conversation where
 * it was made. Only the last turn carries one (a thread of stale cards would be
 * noise), and three things clear it — the next turn, the user's own dismissal,
 * and LEAVING the conversation, which counts as a dismissal (`toUIMessage` does
 * not rehydrate the persisted part). The subject can be proposed again in a
 * later turn; the already-declared filter keeps an accepted INTEREST from coming
 * back. A room the user has joined is NOT filtered — since AC-B4b it is
 * proposed again in the "return" register, which is what makes JuisciGPT an
 * activation channel and not only an acquisition one. What limits repetition
 * there is the §5 frequency policy, server-side.
 *
 * "Already accepted" is DERIVED, not stored: accepting an interest writes the
 * uids into the user's interests, so the same suggestion filters itself out on
 * the next render. That is why the accept path revalidates the interests query.
 */

// A dismissal only has to outlive re-renders, not navigation: leaving the
// conversation already retires the proposal, so nothing durable is needed here.
// Keyed by the assistant message id, which is the live (streamed) id — the ids
// never come back after a remount, so the list cannot grow unbounded in practice.
const useDismissedSuggestions = createMemoryStore<string[]>([]);

/**
 * Record what actually happened to a Room proposal, for the §5 frequency
 * policy — the per-room cooldown, the three-strikes rule and the global cap all
 * read these rows on the next turn.
 *
 * Separate from the Mixpanel events fired alongside it, deliberately: those
 * answer "how is the funnel doing", this answers "may we propose again". The
 * policy must not depend on an analytics pipeline being up.
 *
 * Fire-and-forget, like the article read/view writes: a failure reaches Sentry
 * through the api client's global `onRequestError`, and the worst case is one
 * extra proposal — never a user-visible inconsistency worth a toast.
 */
function recordSuggestionEvent(
  roomId: string,
  kind: "shown" | "clicked" | "dismissed"
): void {
  void apiClient.post("/webapp/private/rooms/:id/suggestion-events", {
    param: { id: roomId },
    json: { kind },
  });
}

export type ChatSuggestion =
  | {
      kind: "topics";
      topics: SuggestedTopic[];
      // The uids the user kept selected. Never called with an empty list — the
      // card's confirm button is disabled until at least one is selected.
      onConfirm: (uids: string[]) => void;
      onDismiss: () => void;
    }
  | {
      kind: "room";
      // ONE room, never a list (AC-A1c). The wire still carries an array
      // because the archived transcript part is historical; the narrowing
      // happens at this boundary so the card cannot render a choice the
      // product no longer offers.
      room: SuggestedRoom;
      // Fired once when the card actually reaches the screen. The IMPRESSION is
      // what the frequency policy rations (§5) — not the server's decision to
      // propose, which this hook may still drop (inside a room, or on a stale
      // archived turn) — so the impression is what gets recorded.
      onShown: () => void;
      // Opens the room. NOT a join: the room's own entry flow owns the
      // explanation and the contact-consent step, and a chat card must never
      // walk a user past them.
      onOpen: () => void;
      onDismiss: () => void;
    }
  | {
      // The paper this conversation is about can be turned into a poster.
      //
      // Client-derived, unlike the two arms above: no server turn proposes it.
      // The paper's eligibility and whether a poster already exists are known
      // from the visual-abstract endpoint, so the surface that owns the paper
      // builds this arm itself. It is in this union because the union IS the
      // band's contract — one proposal at a time, whatever produced it.
      kind: "visual_abstract";
      articleId: string;
      onShown: () => void;
      onGenerate: () => void;
      onDismiss: () => void;
    };

export function useChatSuggestion(
  chat: GptChatInstance,
  // True when this chat is being read from INSIDE a room. A room proposal there
  // would point at a neighbouring partner space while the user is standing in
  // one — the surface is already spoken for. Interests still propose normally.
  insideRoom: boolean,
  // The engine that answers this chat, which the room card's events report.
  engine: ConversationEngine
): ChatSuggestion | null {
  const { t } = useTranslation();
  const history = useHistory();
  const revalidate = useApiRevalidate();
  const [dismissed, setDismissed] = useDismissedSuggestions();
  // The topics card is for readers whose feed ranks by topics, the
  // `DiscoverySurface` rule: outside the practice arm, or in it with no practice
  // held. The card waits for the catalogue read, and a failed read keeps it.
  const arm = usePracticeArm();
  const heldPractices = useApiQuery(
    "/webapp/private/practice-profiles",
    arm === "in" ? {} : false
  );
  const offersTopics =
    arm !== "in" ||
    heldPractices.state === "error" ||
    (heldPractices.state === "success" && heldPractices.body.docs.length === 0);
  const interestsQuery = useApiQuery(
    "/webapp/private/users/me/interests",
    offersTopics ? {} : false
  );

  const last = chat.messages.at(-1);
  if (last === undefined || last.role !== "assistant") return null;
  const messageId = last.id;
  if (dismissed.includes(messageId)) return null;

  const suggestion = uiMessageSuggestion(last);

  function dismiss(): void {
    setDismissed((previous) => [...previous, messageId]);
  }

  switch (suggestion.kind) {
    case "none":
      return null;

    case "rooms": {
      if (insideRoom) return null;
      // The server ranks and caps to one (AC-A1c); an older archived turn may
      // still carry two, in which case the ranked head is the one to show.
      const room = suggestion.rooms.at(0);
      if (room === undefined) return null;
      return {
        kind: "room",
        room,
        onShown: () => {
          gaEventGptRoomSuggestionShown({
            roomId: room.id,
            roomName: room.name,
            isMember: room.isMember,
            trigger: room.trigger,
            citedSourceCount: room.citedSourceCount,
            engine,
          });
          recordSuggestionEvent(room.id, "shown");
        },
        onOpen: () => {
          gaEventGptRoomSuggestionClicked({
            roomId: room.id,
            roomName: room.name,
            isMember: room.isMember,
            trigger: room.trigger,
            citedSourceCount: room.citedSourceCount,
            engine,
          });
          recordSuggestionEvent(room.id, "clicked");
          // Carry the discovery channel in location state so a join made from
          // this room page is attributed to JuisciGPT in `room_joined`
          // (RoomDiscoveryPage reads it via useLocationState → useJoinRoom).
          // Without it the join defaults to "public app" and the funnel this
          // whole feature exists to prove becomes unmeasurable.
          history.push(`/room/discovery/${room.id}`, {
            roomDiscoveryChannel: "juiscigpt",
          });
        },
        onDismiss: () => {
          gaEventGptRoomSuggestionDismissed({
            roomId: room.id,
            roomName: room.name,
            isMember: room.isMember,
            trigger: room.trigger,
            citedSourceCount: room.citedSourceCount,
            engine,
          });
          recordSuggestionEvent(room.id, "dismissed");
          dismiss();
        },
      };
    }

    case "topics": {
      // The write needs the CURRENT selection (the endpoint has full-replace set
      // semantics), so no card until the interests have loaded, and none while
      // `offersTopics` keeps the read idle.
      if (interestsQuery.state !== "success") return null;
      // Captured before the callbacks close over it: the discriminated query
      // result narrows here, not inside an async closure.
      const interests = interestsQuery.body;
      const declaredUids = new Set(interests.topics.map((topic) => topic.uid));
      const topics = suggestion.topics.filter(
        (topic) => !declaredUids.has(topic.uid)
      );
      if (topics.length === 0) return null;

      async function confirm(uids: string[]): Promise<void> {
        try {
          // Full-replace set: the existing selection UNION the accepted uids.
          // This endpoint (not PATCH /users/me/topics) because it is the one
          // that stamps `declaredInterestsUpdatedAt` and kicks the
          // preference-profile rebuild — an interest added mid-conversation
          // should reach the feed tonight.
          await apiClient.put("/webapp/private/users/me/interests", {
            json: {
              topicUids: [...new Set([...declaredUids, ...uids])],
              meshInterests: interests.meshInterests,
            },
          });
          // The interests revalidation is what makes the card disappear (the
          // accepted uids now filter themselves out), which also restores the
          // composer input in its place.
          void revalidate("/webapp/private/users/me/interests");
          void revalidate("/webapp/private/users/me/topics");
          void revalidate("/webapp/private/feed");
          void revalidate("/webapp/private/playlists/recommended");
          displayToast(t("Added to your interests"), "success");
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
        }
      }

      return {
        kind: "topics",
        topics,
        onConfirm: (uids) => {
          void confirm(uids);
        },
        onDismiss: dismiss,
      };
    }

    default:
      throw new Error(suggestion satisfies never);
  }
}
